import { useMarketplace } from 'hooks';
import moment, { Moment } from 'moment-timezone';
import { Dispatch, FunctionComponent, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import MultipleCalendarBooking from './MultipleCalendarBooking';
import DateSelectorComponent from './DateSelectorComponent';
import HoursBooking from './HoursBooking';
import styles from './MultipleDateSelectorSection.module.scss';
import clsx from 'clsx';

interface MultipleDateSelectorSectionProps {
    from: Moment;
    to: Moment;
    selectedDates: Array<Moment>;
    labelDate?: string;
    labelTimeStart?: string;
    labelTimeEnd?: string;
    hideEndTime?: boolean;
    hideTimes?: boolean;
    readOnly?: boolean;
    className?: string;
    onChangeSelectedDates: Dispatch<SetStateAction<Array<Moment>>>;
    onSelectPeriod: (from: Moment, to: Moment) => void;
}

const getDateFormatted = (date: Moment) => {
    const minutes = date.minutes();
    const dateFormatted = date;

    if (minutes > 0 && minutes <= 15) {
        dateFormatted.add(15 - minutes, 'minutes');
    } else if (minutes > 15 && minutes <= 30) {
        dateFormatted.add(30 - minutes, 'minutes');
    } else if (minutes > 30 && minutes <= 45) {
        dateFormatted.add(45 - minutes, 'minutes');
    } else if (minutes > 45 && minutes <= 59) {
        dateFormatted.add(60 - minutes, 'minutes');
    }

    return dateFormatted;
};

const uniqByDay = (dates: Array<Moment>) => {
    const map = new Map<string, Moment>();
    dates.forEach(d => {
        map.set(d.clone().startOf('day').format('YYYY-MM-DD'), d.clone());
    });
    return Array.from(map.values()).sort((a, b) => a.valueOf() - b.valueOf());
};

const displaySelectedLabel = (selectedDates: Array<Moment>, fallback: Moment) => {
    if (selectedDates.length <= 1) {
        const d = selectedDates[0] ?? fallback;
        return d.format('dddd, DD MMM YYYY');
    }
    return `${selectedDates.length} Days Selected`;
};

export const MultipleDateSelectorSection: FunctionComponent<MultipleDateSelectorSectionProps> = props => {
    const marketplace = useMarketplace();

    const [expanded, setExpanded] = useState({
        calendar: false,
        from: false,
        to: false
    });

    const [activeStartDate, setActiveStartDate] = useState<Date | null>(() => getDateFormatted(props.from).toDate());

    const ref = useRef<HTMLDivElement>(null);

    const expandCalendarHandler = () => {
        setExpanded(prev => ({
            ...prev,
            calendar: !prev.calendar,
            from: false,
            to: false
        }));
    };

    const closeCalendarHandler = () => {
        setExpanded(prev => ({ ...prev, calendar: false }));
    };

    const expandFromHandler = () => {
        setExpanded(prev => ({
            ...prev,
            calendar: false,
            from: !prev.from,
            to: false
        }));
    };

    const closeFromHandler = () => {
        setExpanded(prev => ({ ...prev, from: false }));
    };

    const expandToHandler = () => {
        setExpanded(prev => ({
            ...prev,
            calendar: false,
            from: false,
            to: !prev.to
        }));
    };

    const closeToHandler = () => {
        setExpanded(prev => ({ ...prev, to: false }));
    };

    const selectedLabel = useMemo(
        () => displaySelectedLabel(props.selectedDates, props.from),
        [props.selectedDates, props.from]
    );

    const selectFromHandler = (selectedHour: string) => {
        closeFromHandler();
        const from = moment.tz(
            `${getDateFormatted(props.from).format('YYYY-MM-DD')} ${selectedHour.toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );

        props.onSelectPeriod(from, props.hideEndTime ? from.clone() : props.to);
    };

    const selectToHandler = (selectedHour: string) => {
        closeToHandler();
        const to = moment.tz(
            `${getDateFormatted(props.from).format('YYYY-MM-DD')} ${selectedHour.toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );
        props.onSelectPeriod(props.from, to);
    };

    const toggleDate = (selectedDate: Date) => {
        const day = moment.tz(moment(selectedDate).format('YYYY-MM-DD'), 'YYYY-MM-DD', marketplace.schedule.timeZone);
        const key = day.format('YYYY-MM-DD');
        const dayKey = (d: Moment) => d.clone().startOf('day').format('YYYY-MM-DD');
        const deselecting = props.selectedDates.some(d => dayKey(d) === key);
        const remaining = props.selectedDates.filter(d => dayKey(d) !== key);

        props.onChangeSelectedDates(prev => {
            const existing = prev.some(d => dayKey(d) === key);
            return uniqByDay(existing ? prev.filter(d => dayKey(d) !== key) : [...prev, day]);
        });

        // Selecting a day moves `from`/`to` onto it. A deselect leaves them alone, so it does not
        // re-check alerts for a day that is no longer booked, unless a single day remains: that
        // day becomes the booking's date.
        if (deselecting && remaining.length !== 1) return;

        const target = deselecting
            ? moment.tz(
                  remaining[0].clone().startOf('day').format('YYYY-MM-DD'),
                  'YYYY-MM-DD',
                  marketplace.schedule.timeZone
              )
            : day;
        const from = moment.tz(
            `${target.format('YYYY-MM-DD')} ${props.from.format('LT').toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );
        const to = props.hideEndTime
            ? from.clone()
            : moment.tz(
                  `${target.format('YYYY-MM-DD')} ${props.to.format('LT').toUpperCase()}`,
                  'YYYY-MM-DD hh:mm A',
                  marketplace.schedule.timeZone
              );
        props.onSelectPeriod(from, to);
    };

    const selectAllVisible = () => {
        if (!activeStartDate) return;
        const start = moment(activeStartDate).startOf('month');
        const daysInMonth = start.daysInMonth();
        const all = Array.from({ length: daysInMonth }, (_, i) =>
            moment.tz(start.clone().add(i, 'day').format('YYYY-MM-DD'), 'YYYY-MM-DD', marketplace.schedule.timeZone)
        );
        props.onChangeSelectedDates(prev => uniqByDay([...prev, ...all]));
    };

    const clearAll = () => {
        props.onChangeSelectedDates(() => []);
    };

    useEffect(() => {
        ref?.current?.scrollIntoView();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, props.readOnly]);

    // Mantiene la navegación del calendario cerca del último día seleccionado
    useEffect(() => {
        const last = props.selectedDates[props.selectedDates.length - 1];
        if (last) setActiveStartDate(last.toDate());
    }, [props.selectedDates]);

    return (
        <div className={clsx(styles.container, props.className)}>
            <div
                className={clsx(styles.sectionCell, {
                    [styles.marginBottom]: props.className
                })}
            >
                <div
                    ref={ref}
                    className={styles.titleContainer}
                >
                    <span className={styles.title}>{props.labelDate}</span>
                </div>
                <div className={styles.sectionCalendar}>
                    <span className={clsx(styles.text, styles.selectedSummary)}>{selectedLabel}</span>
                    <DateSelectorComponent
                        id="date"
                        expanded={expanded.calendar}
                        className={styles.contentDate}
                        onExpand={expandCalendarHandler}
                        onClickAway={closeCalendarHandler}
                    >
                        <div className={styles.calendarContainer}>
                            <MultipleCalendarBooking
                                activeStartDate={activeStartDate}
                                selectedDates={props.selectedDates}
                                timeZone={marketplace.schedule.timeZone}
                                onActiveStartDateChange={setActiveStartDate}
                                onToggleDate={toggleDate}
                            />
                            <div className={styles.calendarFooter}>
                                <span className={styles.footerText}>{selectedLabel}</span>
                                <div className={styles.footerActions}>
                                    <button
                                        type="button"
                                        className={styles.selectAllButton}
                                        onClick={selectAllVisible}
                                    >
                                        Select All
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.clearButton}
                                        onClick={clearAll}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </DateSelectorComponent>
                </div>
            </div>
            {!props.hideTimes && (
                <div style={{ display: 'flex' }}>
                    <div
                        className={clsx(styles.sectionCell, {
                            [styles.marginBottom]: props.className
                        })}
                    >
                        <div className={styles.titleContainer}>
                            <span className={styles.title}>{props.labelTimeStart}</span>
                        </div>
                        <div className={styles.section}>
                            <span className={clsx(styles.textHours, styles.text)}>
                                {props.from.format('LT').toLowerCase()}
                            </span>
                            <DateSelectorComponent
                                id="hour"
                                expanded={expanded.from}
                                className={styles.contentHours}
                                onExpand={expandFromHandler}
                                onClickAway={closeFromHandler}
                            >
                                <HoursBooking
                                    dayMoment={getDateFormatted(props.from)}
                                    onSelectHour={selectFromHandler}
                                />
                            </DateSelectorComponent>
                        </div>
                    </div>
                    {!props.hideEndTime && (
                        <div
                            className={clsx(styles.sectionCell, styles.spacing, {
                                [styles.marginBottom]: props.className
                            })}
                        >
                            <div className={styles.titleContainer}>
                                <span className={styles.title}>{props.labelTimeEnd}</span>
                            </div>
                            <div className={styles.section}>
                                <span className={clsx(styles.textHours, styles.text)}>
                                    {props.to.format('LT').toLowerCase()}
                                </span>
                                <DateSelectorComponent
                                    id="hour"
                                    expanded={expanded.to}
                                    className={styles.contentHours}
                                    onExpand={expandToHandler}
                                    onClickAway={closeToHandler}
                                >
                                    <HoursBooking
                                        dayMoment={getDateFormatted(props.to)}
                                        onSelectHour={selectToHandler}
                                    />
                                </DateSelectorComponent>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultipleDateSelectorSection;
